import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { listMembers, MemberRow } from '../features/members/api';
import PersonAvatar from './PersonAvatar';

// Buscador global de alumnos en la cabecera (OK Carlos 28-jul, patrón "Buscar paciente"
// de la agenda Jesara). Busca por nombre/teléfono/correo contra /members (status=all:
// los inactivos aparecen con badge — para reactivar o consultar historial se busca igual).
// Atajo: tecla `/` enfoca el buscador desde cualquier página.
const MIN_CHARS = 2;
const MAX_RESULTS = 8;

export default function GlobalSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<MemberRow[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [mobileOpen, setMobileOpen] = useState(false); // input expandido en móvil
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  // Debounce 250ms (mismo patrón que la lista de Alumnos)
  useEffect(() => {
    const term = q.trim();
    if (term.length < MIN_CHARS) { setRows([]); setOpen(false); return; }
    const t = setTimeout(() => {
      listMembers({ search: term, status: 'all', page_size: MAX_RESULTS })
        .then((r) => { setRows(r.data); setOpen(true); setActive(-1); })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Cerrar al navegar y al hacer clic fuera
  useEffect(() => { setOpen(false); setMobileOpen(false); setQ(''); }, [location.pathname]);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) { setOpen(false); setMobileOpen(false); }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Atajo global `/` → enfocar el buscador (si no estás ya escribiendo en otro campo)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      e.preventDefault();
      (inputRef.current ?? mobileInputRef.current)?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const goTo = (r: MemberRow) => { setOpen(false); setMobileOpen(false); setQ(''); navigate(`/alumnos/${r.account_id}`); };
  const goAll = () => { const term = q.trim(); setOpen(false); setMobileOpen(false); setQ(''); navigate(`/alumnos?buscar=${encodeURIComponent(term)}`); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0 && rows[active]) goTo(rows[active]);
      else if (rows.length === 1) goTo(rows[0]);
      else if (rows.length > 0) goAll();
    } else if (e.key === 'Escape') { setOpen(false); setMobileOpen(false); (e.target as HTMLElement).blur(); }
  };

  const input = (ref: React.RefObject<HTMLInputElement>, autoFocus = false) => (
    <div className="relative flex-1">
      <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
      <input
        ref={ref}
        value={q}
        autoFocus={autoFocus}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (rows.length > 0 && q.trim().length >= MIN_CHARS) setOpen(true); }}
        onKeyDown={onKeyDown}
        placeholder="Buscar alumno…"
        className="w-full rounded-xl border border-line bg-surface py-1.5 pl-9 pr-3 text-sm text-content placeholder:text-content-3 outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30"
      />
    </div>
  );

  const dropdown = open && (
    <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-xl">
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-sm text-content-3">Sin resultados para «{q.trim()}».</p>
      ) : (
        <>
          {rows.map((r, i) => (
            <button
              key={r.account_id}
              onMouseDown={(e) => { e.preventDefault(); goTo(r); }}
              onMouseEnter={() => setActive(i)}
              className={`flex w-full items-center gap-3 px-3 py-2 text-left transition ${i === active ? 'bg-surface-2' : ''}`}
            >
              <PersonAvatar contactId={r.account_id} hasPhoto={r.has_photo}
                name={r.firstname} lastName={r.lastname} size="h-8 w-8" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-content">{r.firstname} {r.lastname}</span>
                {r.phone_number && <span className="block truncate text-xs text-content-3">{r.phone_number}</span>}
              </span>
              {!r.active && <span className="shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-[10px] font-medium text-content-3">inactiva</span>}
            </button>
          ))}
          <button
            onMouseDown={(e) => { e.preventDefault(); goAll(); }}
            className="w-full border-t border-line px-4 py-2 text-left text-xs font-medium text-brand-blue transition hover:bg-surface-2"
          >
            Ver todos los resultados →
          </button>
        </>
      )}
    </div>
  );

  return (
    <div ref={boxRef}>
      {/* Escritorio: input fijo en la cabecera */}
      <div className="relative hidden w-[340px] md:block">
        {input(inputRef)}
        {dropdown}
      </div>

      {/* Móvil: lupa que expande el buscador a todo el ancho de la cabecera */}
      <div className="md:hidden">
        {!mobileOpen ? (
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Buscar alumno"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-content-2 ring-1 ring-line transition hover:bg-surface-2"
          >
            <Search size={17} />
          </button>
        ) : (
          <div className="absolute inset-x-0 top-0 z-40 flex h-16 items-center gap-2 bg-canvas px-3">
            {input(mobileInputRef, true)}
            <button onClick={() => { setMobileOpen(false); setQ(''); }} aria-label="Cerrar búsqueda"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-content-2 ring-1 ring-line">
              <X size={17} />
            </button>
            <div className="absolute inset-x-0 top-full px-3"><div className="relative">{dropdown}</div></div>
          </div>
        )}
      </div>
    </div>
  );
}
